import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { InteractiveFormGroup, InteractiveFormValue, PollOptionFormValue } from "../../models/interactive-form.model";

export const FORM_CONSTRAINTS = {
    name: { maxLength: 50 },
    prompt: { maxLength: 280 },
    description: { maxLength: 250 },
    maxCharacterOverride: { min: 1, max: 280 },
    maxDurationOverride: { min: 1, max: 60 },
    maxVideoDurationOverride: { min: 1, max: 30 },
    pollOptions: { min: 2, maxSingleSelect: 4, maxMultiSelect: 500, textMaxLength: 85, csvMaxSizeMb: 1 },
    maxSelectionsAllowed: { min: 2, max: 5 }
} as const;

export type PollOptionStatus = "OK" | "LENGTHY" | "EMPTY" | "DUPLICATE";

export const ERROR_MESSAGES: Partial<Record<keyof InteractiveFormValue, Partial<Record<string, string>>>> = {
    name: {
        required: "Enter a name.",
        maxlength: `Name must be ${FORM_CONSTRAINTS.name.maxLength} characters or fewer.`
    },
    prompt: {
        required: "Enter a listener prompt.",
        maxlength: `Prompt must be ${FORM_CONSTRAINTS.prompt.maxLength} characters or fewer.`
    },
    description: {
        maxlength: `Description must be ${FORM_CONSTRAINTS.description.maxLength} characters or fewer.`
    },
    maxCharacterOverride: {
        min: `Text submissions must be ${FORM_CONSTRAINTS.maxCharacterOverride.min}-${FORM_CONSTRAINTS.maxCharacterOverride.max} characters.`,
        max: `Text submissions must be ${FORM_CONSTRAINTS.maxCharacterOverride.min}-${FORM_CONSTRAINTS.maxCharacterOverride.max} characters.`,
        maxCharacterOverrideRequired: "Enter a max character count."
    },
    maxDurationOverride: {
        min: `Voice submissions must be ${FORM_CONSTRAINTS.maxDurationOverride.min}-${FORM_CONSTRAINTS.maxDurationOverride.max} seconds.`,
        max: `Voice submissions must be ${FORM_CONSTRAINTS.maxDurationOverride.min}-${FORM_CONSTRAINTS.maxDurationOverride.max} seconds.`,
        maxDurationOverrideRequired: "Choose an audio duration."
    },
    maxVideoDurationOverride: {
        min: `Video submissions must be ${FORM_CONSTRAINTS.maxVideoDurationOverride.min}-${FORM_CONSTRAINTS.maxVideoDurationOverride.max} seconds.`,
        max: `Video submissions must be ${FORM_CONSTRAINTS.maxVideoDurationOverride.min}-${FORM_CONSTRAINTS.maxVideoDurationOverride.max} seconds.`,
        maxVideoDurationOverrideRequired: "Choose a video duration."
    },
    startDate: {
        startDateRequired: "Select a start date and time.",
        startDateInPast: "Start date must be in the future."
    },
    endDate: {
        endDateRequired: "Select an end date and time.",
        endDateBeforeStartDate: "End time must be after start time.",
        endDateInPast: "End time must be in the future."
    },
    maxSelectionsAllowed: {
        maxSelectionsExceedsOptions: "Max selections allowed can't exceed the number of poll options."
    }
};

type InteractiveFormErrors = Record<string, true>;

export function interactiveFormValidator(
    fn: (group: InteractiveFormGroup) => InteractiveFormErrors | null
): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => fn(control as InteractiveFormGroup);
}

export const atLeastOneMediaTypeValidator = interactiveFormValidator(group => {
    if (group.controls.type.value !== "freeform") return null;
    const { allowsText, allowsAudio, allowsVideo } = group.controls;
    return allowsText.value || allowsAudio.value || allowsVideo.value ? null : { atLeastOneMediaType: true };
});

export const mediaOverrideRequiredValidator = interactiveFormValidator(group => {
    if (group.controls.type.value !== "freeform") return null;
    const {
        allowsText,
        allowsAudio,
        allowsVideo,
        maxCharacterOverride,
        maxDurationOverride,
        maxVideoDurationOverride
    } = group.controls;
    const errors: InteractiveFormErrors = {};
    if (allowsAudio.value && !maxDurationOverride.value) {
        errors["maxDurationOverrideRequired"] = true;
    }
    if (allowsText.value && !maxCharacterOverride.value) {
        errors["maxCharacterOverrideRequired"] = true;
    }
    if (allowsVideo.value && !maxVideoDurationOverride.value) {
        errors["maxVideoDurationOverrideRequired"] = true;
    }
    return Object.keys(errors).length ? errors : null;
});

export const pollOptionsCountValidator = interactiveFormValidator(group => {
    if (group.controls.type.value !== "poll") return null;
    const { isMultiSelect, pollOptions } = group.controls;
    const count = pollOptions.length;
    const max = isMultiSelect.value
        ? FORM_CONSTRAINTS.pollOptions.maxMultiSelect
        : FORM_CONSTRAINTS.pollOptions.maxSingleSelect;
    const errors: InteractiveFormErrors = {};
    if (count < FORM_CONSTRAINTS.pollOptions.min) {
        errors["pollOptionsMinCount"] = true;
    }
    if (count > max) {
        errors["pollOptionsMaxCount"] = true;
    }
    return Object.keys(errors).length ? errors : null;
});

// Validates the option is not EMPTY, LENGTHY (exceeds max length), or DUPLICATE (same text as another option).
// Classifies every option in one O(n) pass to build a trimmed+lowercased text -> count map, then a
// second O(n) pass to classify each option (the duplicate check is an O(1) lookup, not a re-scan).
export function computePollOptionStatuses(options: readonly PollOptionFormValue[]): PollOptionStatus[] {
    const textCounts = new Map<string, number>();
    for (const option of options) {
        const key = option.text.trim().toLowerCase();
        textCounts.set(key, (textCounts.get(key) ?? 0) + 1);
    }
    return options.map(option => {
        const trimmed = option.text.trim();
        if (!trimmed) return "EMPTY";
        if (option.text.length > FORM_CONSTRAINTS.pollOptions.textMaxLength) return "LENGTHY";
        if ((textCounts.get(trimmed.toLowerCase()) ?? 0) > 1) return "DUPLICATE";
        return "OK";
    });
}

export const pollOptionStatusValidator = interactiveFormValidator(group => {
    if (group.controls.type.value !== "poll") return null;
    const hasIssue = computePollOptionStatuses(group.controls.pollOptions.getRawValue()).some(
        status => status !== "OK"
    );
    return hasIssue ? { pollOptionStatusIssues: true } : null;
});

// A multi-select poll can't allow more selections than it has options.
export const maxSelectionsAllowedValidator = interactiveFormValidator(group => {
    if (group.controls.type.value !== "poll") return null;
    const { isMultiSelect, maxSelectionsAllowed, pollOptions } = group.controls;
    if (!isMultiSelect.value) return null;
    if (maxSelectionsAllowed.value !== null && maxSelectionsAllowed.value > pollOptions.length) {
        return { maxSelectionsExceedsOptions: true };
    }
    return null;
});

export const datesValidator = interactiveFormValidator(group => {
    const { hasStartDate, hasEndDate, startDate, endDate, type, isMultiSelect } = group.controls;
    const errors: InteractiveFormErrors = {};

    const isSingleSelectPoll = type.value === "poll" && !isMultiSelect.value;

    if (hasStartDate.value && !startDate.value) {
        errors["startDateRequired"] = true;
    }
    if ((hasEndDate.value || isSingleSelectPoll) && !endDate.value) {
        errors["endDateRequired"] = true;
    }
    // Skip the in-past check when the control is disabled (e.g. editing an already-started
    // LIVE/PAUSED interactive whose startDate is locked in the past).
    if (startDate.value && !startDate.disabled && new Date(startDate.value) <= new Date()) {
        errors["startDateInPast"] = true;
    }
    if (endDate.value) {
        const end = new Date(endDate.value);
        if (end <= new Date()) {
            errors["endDateInPast"] = true;
        } else if (startDate.value && end <= new Date(startDate.value)) {
            errors["endDateBeforeStartDate"] = true;
        }
    }
    return Object.keys(errors).length ? errors : null;
});
